import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Popup } from "@progress/kendo-react-popup";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { debounce } from "lodash";
import { makeExistingTaskDescChanged, setExpanded, toggleDescPopup } from "../../../../actions/taskSidebar";
import { icon, label, notifyIcon, number, quote } from "../../../../config";
import KendoEditor from "../../../../editor/KendoEditor";
import { editorToolType } from "../../../../editor/editor.Constant";
import EditorMode from "../../../../editor/editorTools/EditorMode";
import { usePopup } from "../../../../helper/commonHooks";
import { getNotification, isTrueBit } from "../../../../utils/common";
import { addUpdateDescLog, handleTaskKeyUpdate } from "../../../Tasks/tasks.service";
import { isFieldDefined } from "../../sidebar.common";
import { DESC_TYPE } from "../../sidebar.constants";
import { setSidebarFieldsDisabled } from "../../sidebar.helper";
import DescriptionHistory from "./DescriptionHistory";
import "./descriptionPopup.scss";
import VersionPreview from "./VersionPreview";
/**
 * Description Popup component
 * @param {Object} props
 * @returns JSX
 * @author Himanshu Negi
 */
const DescriptionPopup = ({ provider, setProvider }) => {
  const [saveStatus, setSaveStatus] = useState("");
  const [desc, setDesc] = useState("");
  const [autoSaveKey, setUpdateAutoSaveKey] = useState(number.ONE);
  const editableRef = useRef(false);
  const viewRef = useRef(null);
  const editorRef = useRef(null);
  const yjsDocRef = useRef(null);
  const dispatch = useDispatch();
  const { task, isNewTask, newTaskData, expanded, showDescPopup, isExistingTaskDescChanged, isYdocexists, tempYdocRoom } = useSelector(
    (state) => state.taskSidebar
  );
  const { user } = useSelector((state) => state.auth);
  const { isPlanMyDayActive } = useSelector((state) => state.planMyDay);
  const { workflowFeatures } = useSelector((state) => state.taskWorkflowDetails);
  const defaultDesc = "<p> </p>";
  const { anchor } = usePopup();
  const [editorState, setEditorState] = useState("disconnected");
  const [refreshKey, setRefreshKey] = useState(true);
  const [versionPreviewMode, setVersionPreviewMode] = useState(
    {
      show: false, 
      versionData: null, 
      key: number.ONE, 
      loading: false,
    }
  );
  const [restoredHtml, setRestoredHtml] = useState(null);
  const [restoredHtmlApplyNonce, setRestoredHtmlApplyNonce] = useState({
    value: number.ZERO,
    idToIdentify: null,
  });
  const versionViewRef = useRef(null);
  // Use useMemo to create the collab object
  const collab = useMemo(() => {
    if (task && showDescPopup?.type === DESC_TYPE.DESCRIPTION) {
      // in case of approval description, we are not using collab
      return {
        value: true,
        isYdocExists: true,
        YdocRoom: isNewTask ? String(tempYdocRoom) : String(task.taskId),
      };
    }
    return null;
  }, [isNewTask, isYdocexists, task?.taskId, user.id]);

  /**
   * edit condition for editor desc popup
   * @author Shivam Mishra
   */
  useEffect(() => {
    const isEditableCondition = () => {
      if (showDescPopup?.type === DESC_TYPE.DESCRIPTION) {
        const shouldEdit = isNewTask || (!task.isFollowed && (isTrueBit(task, "InApproval") || !(task?.IsTaskComplete || task?.IsTaskClosed)));
        return shouldEdit ? true : false;
      } else {
        return !setSidebarFieldsDisabled();
      }
    };
    if (autoSaveKey === number.ONE) {
      if (isEditableCondition()) {
        editableRef.current = true;
      } else {
        editableRef.current = false;
      }
    }
  }, [isNewTask, task?.isFollowed, task?.IsTaskComplete, task?.IsTaskClosed, showDescPopup?.type]);

  /**
   * Initialize description when task changes or workflow features update
   * @author Abhishek Dhillon
   */
  useEffect(() => {
    initializeDescription();
  }, [
    autoSaveKey,
    isNewTask,
    task?.ApprovalDescription,
    task?.Description,
    newTaskData?.description,
    showDescPopup?.type,
    workflowFeatures,
    isYdocexists,
  ]);

  /**
   * Set appropriate description based on task type and state
   * @author Abhishek Dhillon
   */
  const initializeDescription = () => {
    if (showDescPopup?.type === DESC_TYPE.APPROVAL_DESCRIPTION && task?.ApprovalDescription) {
      setDesc(task.ApprovalDescription);
      return;
    } else {
      if ((isNewTask || !isYdocexists) && (task?.Description || newTaskData?.description)) {
        const features = Array.isArray(workflowFeatures) ? workflowFeatures : [];
        const featureFive = features?.find((f) => f?.Id === number.FIVE);
        if (isNewTask && featureFive?.IsSelected === number.ONE && showDescPopup?.type === DESC_TYPE.DESCRIPTION) {
          setDesc(getSafeDescription(newTaskData?.description));
          return;
        }
        if (isNewTask && showDescPopup?.type === DESC_TYPE.DESCRIPTION) {
          setDesc(getSafeDescription(newTaskData?.description));
          return;
        }
        if (!isNewTask && showDescPopup?.type === DESC_TYPE.DESCRIPTION && task?.Description) {
          setDesc(getSafeDescription(task.Description));
        }
      } else {
        setDesc(getSafeDescription(task?.Description));
      }
    }
  };

  const getSafeDescription = (data) => data || defaultDesc;

  /**
   * updates description when auto save key gets updated
   * @author Abhishek Dhillon
   */
  useEffect(() => {
    autoSaveKey > number.ONE && updateDescription(desc);
  }, [autoSaveKey]);

  /**
   * It updates description on state and does api call for update description
   * @author Abhishek Dhillon
   */
  const updateDescription = async (newDescText) => {
    setSaveStatus(quote.AUTO_SAVE_LOADING);
    // isNewTask && handleTaskKeyUpdate(true, "isDescChanged", true);
    !isNewTask && showDescPopup?.type === DESC_TYPE.DESCRIPTION && dispatch(makeExistingTaskDescChanged());
    const descKey = showDescPopup?.type === DESC_TYPE.DESCRIPTION ? "description" : "approvalDescription";
    if (newDescText) {
      await handleTaskKeyUpdate(isNewTask, descKey, newDescText);
    }
    if (isNewTask) {
      debouncedSetSaveStatus();
    } else {
      setSaveStatus(quote.AUTO_SAVED);
    }
  };

  const debouncedSetSaveStatus = debounce(() => {
    setSaveStatus(quote.AUTO_SAVED);
  }, number.FIVE_HUNDRED);

  /**
   * Expands Descrition section on taskSidebar
   * @param {String} expandId
   * @author Himanshu Negi
   */
  const expandDescSection = async (expandId) => {
    const isDescriptionOpen = expanded?.find((item) => item === expandId);
    if (!isDescriptionOpen) dispatch(setExpanded([...expanded, expandId]));
  };

  /**
   * It closes description popup and open the description section on TaskSidebar
   * @param {Void}
   * @returns {Void}
   * @author Himanshu Negi
   */
  const closeDescriptionPopup = () => {
    if (isPlanMyDayActive) return getNotification(quote.PLAN_MY_DAY_MODE, notifyIcon.WARNING_ICON);
    showDescPopup?.type === DESC_TYPE.DESCRIPTION ? expandDescSection("task-description") : expandDescSection("task-approval-description");
    dispatch(toggleDescPopup({ status: false, type: "" }));
    isExistingTaskDescChanged &&
      dispatch(
        addUpdateDescLog(
          {
            taskId: task?.taskId,
            userId: user.id,
          },
          true
        )
      );
  };

  // Render the saved text
  const getStatus = () => {
    if (editorState === "connected") {
      if (!saveStatus)
        return (
          <>
            <div className='d-flex align-items-center justify-content-start'>
              <span className='cursor-pointer mr-2 app-icon-medium app-icon-box' onClick={handleRefresh} title={label.REFRESH}>
                {icon.REFRESH}
              </span>
              <span className='cursor-pointer mr-2' onClick={handleRefresh}>
                {label.REFRESH}
              </span>
            </div>
          </>
        );
      if (saveStatus === quote.AUTO_SAVE_LOADING) {
        return (
          <>
            <div className='d-flex align-items-center justify-content-start'>
              <span className='cursor-pointer mr-2 app-icon-medium app-icon-box' title={label.REFRESH}>
                {icon.REFRESH}
              </span>
              <span className='cursor-pointer mr-2'>{saveStatus}</span>
            </div>
          </>
        );
      } else {
        return (
          <div className='d-flex align-items-center justify-content-start'>
            <span className='cursor-pointer mr-2 app-icon-medium app-icon-box' onClick={handleRefresh} title={label.REFRESH}>
              {icon.REFRESH}
            </span>
            <span className='cursor-pointer mr-2' onClick={handleRefresh}>
              {saveStatus}
            </span>
          </div>
        );
      }
    } else {
      if (editorState === "disconnected") {
        return (
          <div className='d-flex align-items-center maroon justify-content-start'>
            <span className='cursor-pointer mr-2 app-icon-medium app-icon-box' onClick={reconnectEditor} title={label.RECONNECT}>
              {icon.SYNC}
            </span>
            <span className='cursor-pointer mr-2' onClick={reconnectEditor}>
              {label.OFFLINE}
            </span>
          </div>
        );
      }
      return editorState;
    }
  };
  const handleRefresh = () => {
    // Unmount + remount: toggle showEditor off/on to recreate the component
    setRefreshKey(false);
    // next tick: remount
    setTimeout(() => {
      setRefreshKey(true);
      setVersionPreviewMode({show: false, versionData: null, key: number.ZERO});
    }, 100);
  };

  const reconnectEditor = () => {
    if (provider?.synced && provider?.wsconnected && Boolean(provider?.wsLastMessageReceived)) return;
    provider?.connect();
    setEditorState("connecting");
  };

  /**
   * Determines if the editor should be displayed
   * @returns {boolean}
   * @author Abhishek Dhillon
   */
  const shouldDisplayEditor = () => {
    if(versionPreviewMode?.show) {
      return false;
    }
    if (showDescPopup?.type === DESC_TYPE.APPROVAL_DESCRIPTION) {
      return Boolean(desc);
    }
    return (isNewTask && desc) || !isNewTask;
  };

  return (
    <React.Fragment>
      <Popup
        show={showDescPopup?.status}
        anchor={anchor.current}
        className='dt-popup desc-popup-container'
        popupClass={"desc-popup-child"}
        animate={false}>
        <div className='desc-popup-content' data-testid='descpopup-testid'>
          <Tooltip anchorElement='target' position='bottom' parentTitle='true'>
            <div className='dt-popup-header d-flex justify-content-between align-items-center pt-2 pb-2 pl-3 pr-3'>
              <div className='dt-popup-title'>{showDescPopup?.type === DESC_TYPE.DESCRIPTION ? label.DESCRIPTION : label.APPROVED_DESCRIPTION}</div>
              <span className='small width-130'>{getStatus()}</span>
              {showDescPopup?.type === DESC_TYPE.DESCRIPTION &&
                yjsDocRef &&
                Boolean(!task.isFollowed) &&
                !(task?.IsTaskComplete || task?.IsTaskClosed) &&
                editableRef.current && (
                  <DescriptionHistory
                    restoredHtml={restoredHtml}
                    setRestoredHtml={setRestoredHtml}
                    restoredHtmlApplyNonce={restoredHtmlApplyNonce}
                    setRestoredHtmlApplyNonce={setRestoredHtmlApplyNonce}
                    provider={provider}
                    editorState={editorState}
                    yjsDocRef={yjsDocRef}
                    desc={desc}
                    setDesc={setDesc}
                    viewRef={viewRef}
                    editableRef={editableRef}
                    editorRef={editorRef}
                    collab={collab}
                    versionPreviewMode={versionPreviewMode}
                    setVersionPreviewMode={setVersionPreviewMode}
                    handleRefresh={handleRefresh}
                    versionViewRef={versionViewRef}
                  />
                )}
              <EditorMode />
              <span className='dt-popup-cancel-btn' onClick={closeDescriptionPopup} title={label.CLOSE_POPUP}>
                {icon.CLOSE}
              </span>
            </div>
          </Tooltip>
          {refreshKey && (
            <div className='dt-popup-body p-0' id='desc-popup' key={versionPreviewMode?.key}>
              <div className='desc-popup-editor p-0 flex-grow-1'>
                {shouldDisplayEditor() ? (
                  <KendoEditor
                    desc={desc}
                    setDesc={setDesc}
                    viewRef={viewRef}
                    editableRef={editableRef}
                    editorRef={editorRef}
                    editorEditIcon={number.FOUR}
                    autoSaveKey={autoSaveKey}
                    setUpdateAutoSaveKey={setUpdateAutoSaveKey}
                    toolbar={editorToolType.TASK_POPUP_TOOL}
                    collab={showDescPopup?.type === DESC_TYPE.DESCRIPTION ? collab : false}
                    provider={provider}
                    setProvider={setProvider}
                    taskKey={"descriptionPopUp"}
                    yjsDocRef={yjsDocRef}
                    showEditIcons={false}
                    setEditorState={setEditorState}
                  />
                ) : (
                  <VersionPreview 
                  versionPreviewMode={versionPreviewMode} 
                  className="w-100" 
                  contentStyle={{ height: "100%" }} 
                  versionViewRef={versionViewRef}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </Popup>
    </React.Fragment>
  );
};

export default DescriptionPopup;
